import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getStateForCity } from '../lib/cityStateMap';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import MultiSelectFilter from './MultiSelectFilter';
import api from '../lib/api';
import SecurityWatermark from './SecurityWatermark';
import { isRestricted, maskPhone, maskEmail, maskGST, applyRestrictedSearch, MIN_SEARCH_CHARS } from '../lib/restrictions';

import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';

const VendorManagement = ({ user }) => {
  const [vendors, setVendors] = useState([]);
  const bulk = useBulkSelect();
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canEdit = isAdmin || user?.role === 'salesman';
  const canDelete = isAdmin;

  const handleDownload = async (format) => {
    try {
      const res = await api.get(`/downloads/vendors/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `vendors.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };
  
  // Edit state
  const [editingVendor, setEditingVendor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editAreas, setEditAreas] = useState([]);

  // View, filter, sort state
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'card'
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStates, setFilterStates] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [filterAreas, setFilterAreas] = useState([]);
  const [filterRatings, setFilterRatings] = useState([]);
  const [sortBy, setSortBy] = useState('name_asc');
  
  const [vendorForm, setVendorForm] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    accountant_phone: '',
    address: '',
    area: '',
    city: '',
    state: '',
    pincode: '',
    gst_number: ''
  });
  const [availableAreas, setAvailableAreas] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const fileInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    fetchVendors();
    // Detect if user is on mobile device
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/vendors');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVendorForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'city') {
        const autoState = getStateForCity(value);
        if (autoState && !prev.state) next.state = autoState;
      }
      return next;
    });
    
    // Auto-fetch city and state when pincode is entered
    if (name === 'pincode' && value.length === 6) {
      fetchPincodeDetails(value);
    }
  };

  const fetchPincodeDetails = async (pincode) => {
    try {
      const response = await api.get(`/pincode/${pincode}`);
      const { areas, area, city, state } = response.data;
      
      setAvailableAreas(areas || []);
      setVendorForm(prev => ({
        ...prev,
        area: area || '',
        city: city || '',
        state: state || ''
      }));
      
      toast.success(`Auto-filled: ${area}, ${city}, ${state}`);
    } catch (error) {
      console.error('Error fetching pincode details:', error);
      if (error.response?.status === 404) {
        toast.error('Pincode not found. Please enter details manually.');
      }
    }
  };

  const handleScanBill = async (file) => {
    if (!file) return;
    
    setIsScanning(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/vendors/scan-bill', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const extractedData = response.data;
      
      // Auto-fill form with extracted data
      setVendorForm(prev => ({
        ...prev,
        name: extractedData.name || prev.name,
        contact_person: extractedData.contact_person || prev.contact_person,
        email: extractedData.email || prev.email,
        phone: extractedData.phone || prev.phone,
        accountant_phone: extractedData.accountant_phone || prev.accountant_phone,
        address: extractedData.address || prev.address,
        city: extractedData.city || prev.city,
        state: extractedData.state || prev.state,
        pincode: extractedData.pincode || prev.pincode,
        gst_number: extractedData.gst_number || prev.gst_number
      }));
      
      toast.success(`Bill scanned! Auto-filled vendor details (${Math.round(extractedData.confidence_score * 100)}% confidence)`);
    } catch (error) {
      console.error('Error scanning bill:', error);
      toast.error('Failed to scan bill. Please enter details manually.');
    } finally {
      setIsScanning(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleScanBill(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/vendors', vendorForm);
      
      toast.success('Vendor created successfully!');
      setIsDialogOpen(false);
      resetForm();
      fetchVendors();
    } catch (error) {
      console.error('Error creating vendor:', error);
      const message = error.response?.data?.detail || 'Failed to create vendor';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setVendorForm({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      accountant_phone: '',
      address: '',
      area: '',
      city: '',
      state: '',
      pincode: '',
      gst_number: ''
    });
    setAvailableAreas([]);
  };

  // Edit vendor
  const openEditVendor = (vendor) => {
    setEditForm({
      name: vendor.name || '', contact_person: vendor.contact_person || '', email: vendor.email || '',
      phone: vendor.phone || '', accountant_phone: vendor.accountant_phone || '',
      address: vendor.address || '', area: vendor.area || '', city: vendor.city || '',
      state: vendor.state || '', pincode: vendor.pincode || '', gst_number: vendor.gst_number || '',
    });
    setEditAreas([]);
    setEditingVendor(vendor);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'city') {
        const autoState = getStateForCity(value);
        if (autoState && !prev.state) next.state = autoState;
      }
      return next;
    });
    if (name === 'pincode' && value.length === 6) {
      api.get(`/pincode/${value}`).then(res => {
        const { areas, area, city, state } = res.data;
        setEditAreas(areas || []);
        setEditForm(prev => ({ ...prev, area: area || prev.area, city: city || prev.city, state: state || prev.state }));
      }).catch(() => {});
    }
  };

  const saveEditVendor = async () => {
    try {
      await api.put(`/vendors/${editingVendor.id}`, editForm);
      toast.success(`Vendor "${editForm.name}" updated!`);
      setEditingVendor(null);
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update vendor'); }
  };

  // Delete vendor
  const deleteVendor = async (vendorId, vendorName) => {
    try {
      await api.delete(`/vendors/${vendorId}`);
      toast.success(`Vendor "${vendorName}" deleted!`);
      fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete vendor'); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  };

  const canManageVendors = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.role === 'inventory_staff';

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSave: () => { if (editingVendor) saveEditVendor(); },
    onEscape: () => { if (editingVendor) setEditingVendor(null); else if (isDialogOpen) setIsDialogOpen(false); },
    onNew: () => { if (!isDialogOpen && canManageVendors) setIsDialogOpen(true); },
  });

  // Derived filter options
  const allStates = [...new Set(vendors.map(v => v.state).filter(Boolean))].sort();
  const allCities = [...new Set(vendors.filter(v => !filterStates.length || filterStates.includes(v.state)).map(v => v.city).filter(Boolean))].sort();
  const allAreas = [...new Set(vendors.map(v => v.area).filter(Boolean))].sort();
  const ratingBuckets = [
    { value: '4-5', label: '4-5 (Excellent)' },
    { value: '3-4', label: '3-4 (Good)' },
    { value: '2-3', label: '2-3 (Average)' },
    { value: '0-2', label: '0-2 (Poor)' },
  ];

  // Filter & sort
  const restricted = isRestricted(user);
  const filteredVendors = restricted
    ? applyRestrictedSearch(user, vendors, searchQuery)
    : vendors.filter(v => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!v.name.toLowerCase().includes(q) && !(v.contact_person || '').toLowerCase().includes(q) && !(v.phone || '').includes(q) && !(v.city || '').toLowerCase().includes(q) && !(v.area || '').toLowerCase().includes(q) && !(v.state || '').toLowerCase().includes(q)) return false;
    }
    if (filterStates.length > 0 && !filterStates.includes(v.state)) return false;
    if (filterCities.length > 0 && !filterCities.includes(v.city)) return false;
    if (filterAreas.length > 0 && !filterAreas.includes(v.area)) return false;
    if (filterRatings.length > 0) {
      const r = v.performance_rating || 0;
      const match = filterRatings.some(bucket => {
        if (bucket === '4-5') return r >= 4;
        if (bucket === '3-4') return r >= 3 && r < 4;
        if (bucket === '2-3') return r >= 2 && r < 3;
        if (bucket === '0-2') return r < 2;
        return false;
      });
      if (!match) return false;
    }
    return true;
  });

  const sortedVendors = [...filteredVendors].sort((a, b) => {
    switch (sortBy) {
      case 'name_asc': return a.name.localeCompare(b.name);
      case 'name_desc': return b.name.localeCompare(a.name);
      case 'city': return (a.city || '').localeCompare(b.city || '');
      case 'state': return (a.state || '').localeCompare(b.state || '');
      case 'rating_high': return (b.performance_rating || 0) - (a.performance_rating || 0);
      case 'rating_low': return (a.performance_rating || 0) - (b.performance_rating || 0);
      case 'newest': return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'oldest': return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      default: return 0;
    }
  });

  const hasActiveFilters = searchQuery || filterStates.length > 0 || filterCities.length > 0 || filterAreas.length > 0 || filterRatings.length > 0;
  const clearAllFilters = () => { setSearchQuery(''); setFilterStates([]); setFilterCities([]); setFilterAreas([]); setFilterRatings([]); };

  const getRatingColor = (r) => {
    if (r >= 4) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (r >= 3) return 'text-blue-700 bg-blue-50 border-blue-200';
    if (r >= 2) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    return 'text-red-700 bg-red-50 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="vendor-management-container">
      {restricted && <SecurityWatermark user={user} label="VENDORS • CONFIDENTIAL" />}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>
            🏭 Vendor Management
          </h1>
          <p className="text-gray-600 mt-2">Manage your supplier network and partnerships</p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" className="text-green-700 border-green-300 hover:bg-green-50 gap-1.5" onClick={() => handleDownload('excel')} data-testid="download-vendors-excel">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Excel
              </Button>
              <Button variant="outline" size="sm" className="text-red-700 border-red-300 hover:bg-red-50 gap-1.5" onClick={() => handleDownload('pdf')} data-testid="download-vendors-pdf">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                PDF
              </Button>
            </>
          )}
        
        {canManageVendors && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Vendor</DialogTitle>
                <DialogDescription>
                  Add a new vendor to your supplier network
                </DialogDescription>
                {isMobile && (
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isScanning}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      size="sm"
                    >
                      {isScanning ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Scanning...
                        </div>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Scan Bill
                        </>
                      )}
                    </Button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept="image/*"
                      capture="environment"
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Company Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={vendorForm.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Kanchipuram Silks Ltd."
                      required
                      data-testid="vendor-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contact_person">Contact Person *</Label>
                    <Input
                      id="contact_person"
                      name="contact_person"
                      value={vendorForm.contact_person}
                      onChange={handleInputChange}
                      placeholder="e.g., Raj Kumar"
                      required
                      data-testid="vendor-contact-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={vendorForm.email}
                      onChange={handleInputChange}
                      placeholder="contact@vendor.com (optional)"
                      data-testid="vendor-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={vendorForm.phone}
                      onChange={handleInputChange}
                      placeholder="+91 9876543210"
                      required
                      data-testid="vendor-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="accountant_phone">Accountant Contact Number</Label>
                  <Input
                    id="accountant_phone"
                    name="accountant_phone"
                    value={vendorForm.accountant_phone}
                    onChange={handleInputChange}
                    placeholder="+91 9876543211"
                    data-testid="vendor-accountant-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={vendorForm.address}
                    onChange={handleInputChange}
                    placeholder="Street address"
                    required
                    data-testid="vendor-address-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode * (Auto-fills Area, City & State)</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      value={vendorForm.pincode}
                      onChange={handleInputChange}
                      placeholder="e.g., 400028"
                      maxLength="6"
                      required
                      data-testid="vendor-pincode-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="area">Area *</Label>
                    {availableAreas.length > 0 ? (
                      <select
                        id="area"
                        name="area"
                        value={vendorForm.area}
                        onChange={handleInputChange}
                        required
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        data-testid="vendor-area-select"
                      >
                        {availableAreas.map((a, i) => (
                          <option key={i} value={a}>{a}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        id="area"
                        name="area"
                        value={vendorForm.area}
                        onChange={handleInputChange}
                        placeholder="Auto-filled from pincode"
                        required
                        data-testid="vendor-area-input"
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={vendorForm.city}
                      onChange={handleInputChange}
                      placeholder="Auto-filled from pincode"
                      required
                      data-testid="vendor-city-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={vendorForm.state}
                      onChange={handleInputChange}
                      placeholder="Auto-filled from pincode"
                      required
                      data-testid="vendor-state-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gst_number">GST Number</Label>
                  <Input
                    id="gst_number"
                    name="gst_number"
                    value={vendorForm.gst_number}
                    onChange={handleInputChange}
                    placeholder="e.g., 33AAACR4849L1ZM"
                    data-testid="vendor-gst-input"
                  />
                </div>

                <div className="flex gap-4 pt-6">
                  <Button type="submit" className="flex-1" data-testid="submit-vendor-button">
                    Create Vendor
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Search vendor, contact, city, state..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 h-10" data-testid="vendor-search-input" />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[150px]" data-testid="vendor-sort-select">
          <option value="name_asc">Name (A-Z)</option>
          <option value="name_desc">Name (Z-A)</option>
          <option value="city">City</option>
          <option value="state">State</option>
          <option value="rating_high">Rating (High)</option>
          <option value="rating_low">Rating (Low)</option>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
        <MultiSelectFilter label="State" testId="vendor-filter-state" selected={filterStates} onChange={setFilterStates} options={allStates.map(s => ({ value: s, label: s }))} />
        <MultiSelectFilter label="City" testId="vendor-filter-city" selected={filterCities} onChange={setFilterCities} options={allCities.map(c => ({ value: c, label: c }))} />
        <MultiSelectFilter label="Area" testId="vendor-filter-area" selected={filterAreas} onChange={setFilterAreas} options={allAreas.map(a => ({ value: a, label: a }))} />
        <MultiSelectFilter label="AI Rating" testId="vendor-filter-rating" selected={filterRatings} onChange={setFilterRatings} options={ratingBuckets} />
        {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearAllFilters} data-testid="vendor-clear-filters">Clear All</Button>}
        <div className="ml-auto flex gap-1">
          <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-gray-600'}`} data-testid="vendor-view-list">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
          </button>
          <button onClick={() => setViewMode('card')} className={`p-2 rounded-md transition-colors ${viewMode === 'card' ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-gray-600'}`} data-testid="vendor-view-card">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
          </button>
        </div>
      </div>

      {/* Vendors Content */}
      <Card data-testid="vendors-list">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <CardTitle>Vendors ({sortedVendors.length}{sortedVendors.length !== vendors.length ? ` of ${vendors.length}` : ''})</CardTitle>
            {canManageVendors && (
              <Button size="sm" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm" onClick={() => setIsDialogOpen(true)} data-testid="add-vendor-button">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                Add Vendor
              </Button>
            )}
          </div>
          <CardDescription>Your supplier network and partnerships</CardDescription>
        </CardHeader>
        <CardContent>
          {sortedVendors.length === 0 ? (
            <div className="text-center py-12" data-testid="vendors-empty">
              {restricted ? (
                (searchQuery || '').trim().length < MIN_SEARCH_CHARS ? (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Search Required</h3>
                    <p className="text-gray-600">Type at least {MIN_SEARCH_CHARS} characters to look up a vendor.</p>
                    <p className="text-xs text-gray-400 mt-2">For your role, the full vendor list is not displayed. Up to 10 search results are shown.</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Match</h3>
                    <p className="text-gray-600">No vendor matches "{searchQuery}".</p>
                  </>
                )
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{vendors.length === 0 ? 'No Vendors Yet' : 'No vendors match filters'}</h3>
                  <p className="text-gray-600 mb-4">{vendors.length === 0 ? 'Start building your supplier network.' : 'Try adjusting search or filters.'}</p>
                  {vendors.length === 0 && canManageVendors && <Button onClick={() => setIsDialogOpen(true)} className="bg-blue-600">Add Your First Vendor</Button>}
                </>
              )}
            </div>
          ) : viewMode === 'list' ? (
            /* ===== TABLE / LIST VIEW ===== */
            <div className="overflow-x-auto" data-testid="vendor-table-view">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-2 py-3 w-10"><input type="checkbox" className="w-4 h-4 rounded" onChange={e => { if (e.target.checked) bulk.selectAll(filteredVendors.map(x => x.id)); else bulk.clearSelection(); }} /></th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Vendor</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Contact</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Location</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">GST</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Rating</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Added</th>
                    {(canEdit || canDelete) && <th className="text-center px-4 py-3 font-semibold text-gray-600">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedVendors.map(vendor => (
                    <tr key={vendor.id} className={`border-b hover:bg-gray-50/80 transition-colors ${bulk.isSelected(vendor.id) ? 'bg-blue-50' : ''}`} data-testid={`vendor-row-${vendor.id}`}>
                      <td className="px-2 py-3" onClick={e => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(vendor.id)} onChange={() => bulk.toggleSelect(vendor.id)} /></td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{vendor.name}</div>
                        {vendor.email && <div className="text-xs text-gray-500 mt-0.5">{restricted ? maskEmail(vendor.email) : vendor.email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700">{vendor.contact_person}</div>
                        <div className="text-xs text-gray-500">{restricted ? maskPhone(vendor.phone) : vendor.phone}</div>
                        {vendor.accountant_phone && <div className="text-xs text-gray-400">Acct: {restricted ? maskPhone(vendor.accountant_phone) : vendor.accountant_phone}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700">{vendor.city}, {vendor.state}</div>
                        {vendor.area && <div className="text-xs text-gray-500">{vendor.area}</div>}
                        <div className="text-xs text-gray-400">{vendor.pincode}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-gray-600">{restricted ? (vendor.gst_number ? maskGST(vendor.gst_number) : '-') : (vendor.gst_number || '-')}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${getRatingColor(vendor.performance_rating)}`}>
                          {(vendor.performance_rating || 0).toFixed(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(vendor.created_at)}</td>
                      {(canEdit || canDelete) && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex gap-1 justify-center">
                            {canEdit && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-600 hover:bg-blue-50" onClick={() => openEditVendor(vendor)} data-testid={`edit-vendor-${vendor.id}`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600 hover:bg-emerald-50" onClick={() => navigate(`/ledger-report?type=vendor&id=${vendor.id}`)} data-testid={`ledger-vendor-${vendor.id}`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            </Button>
                            {canDelete && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50" data-testid={`delete-vendor-${vendor.id}`}>
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Vendor</AlertDialogTitle>
                                    <AlertDialogDescription>Delete "{vendor.name}"? Vendors with active POs cannot be deleted.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => deleteVendor(vendor.id, vendor.name)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-vendor-${vendor.id}`}>Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* ===== CARD VIEW ===== */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vendor-card-view">
              {sortedVendors.map((vendor) => (
                <Card key={vendor.id} className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-emerald-500" data-testid={`vendor-card-${vendor.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg font-bold text-gray-900">{vendor.name}</CardTitle>
                        <p className="text-sm text-gray-600 mt-1">{vendor.contact_person}</p>
                      </div>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${getRatingColor(vendor.performance_rating)}`}>
                        {(vendor.performance_rating || 0).toFixed(1)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-gray-600">
                    {vendor.email && <div className="flex items-center gap-2"><svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg><span className="truncate">{vendor.email}</span></div>}
                    <div className="flex items-center gap-2"><svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>{vendor.phone}</div>
                    <div className="flex items-center gap-2"><svg className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg><span>{vendor.city}, {vendor.state} {vendor.area ? `- ${vendor.area}` : ''}</span></div>
                    {vendor.gst_number && <div className="text-xs text-gray-500 pt-1 border-t border-gray-100">GST: <span className="font-mono">{restricted ? maskGST(vendor.gst_number) : vendor.gst_number}</span></div>}
                    <div className="text-xs text-gray-400 pt-1 border-t border-gray-100">Added {formatDate(vendor.created_at)}</div>
                    {(canEdit || canDelete) && (
                      <div className="pt-1 flex gap-2">
                        {canEdit && <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50 flex-1 h-7 text-xs" onClick={() => openEditVendor(vendor)} data-testid={`edit-vendor-${vendor.id}`}>Edit</Button>}
                        <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 flex-1 h-7 text-xs" onClick={() => navigate(`/ledger-report?type=vendor&id=${vendor.id}`)} data-testid={`ledger-card-vendor-${vendor.id}`}>Ledger</Button>
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50 h-7 text-xs" data-testid={`delete-vendor-${vendor.id}`}>Delete</Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete Vendor</AlertDialogTitle><AlertDialogDescription>Delete "{vendor.name}"? Vendors with active POs cannot be deleted.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteVendor(vendor.id, vendor.name)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-vendor-${vendor.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Vendor Dialog */}
      {editingVendor && (
        <Dialog open={!!editingVendor} onOpenChange={() => setEditingVendor(null)}>
          <DialogContent className="max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Vendor</DialogTitle>
              <DialogDescription>Update vendor details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Company Name *</Label><Input name="name" value={editForm.name} onChange={handleEditChange} required data-testid="edit-vendor-name" /></div>
                <div className="space-y-2"><Label>Contact Person *</Label><Input name="contact_person" value={editForm.contact_person} onChange={handleEditChange} required data-testid="edit-vendor-contact" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Email</Label><Input name="email" type="email" value={editForm.email} onChange={handleEditChange} /></div>
                <div className="space-y-2"><Label>Phone *</Label><Input name="phone" value={editForm.phone} onChange={handleEditChange} required data-testid="edit-vendor-phone" /></div>
              </div>
              <div className="space-y-2"><Label>Accountant Phone</Label><Input name="accountant_phone" value={editForm.accountant_phone} onChange={handleEditChange} /></div>
              <div className="space-y-2"><Label>Address *</Label><Input name="address" value={editForm.address} onChange={handleEditChange} required /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pincode *</Label>
                  <Input name="pincode" value={editForm.pincode} onChange={handleEditChange} maxLength="6" required data-testid="edit-vendor-pincode" />
                </div>
                <div className="space-y-2">
                  <Label>Area</Label>
                  {editAreas.length > 0 ? (
                    <select name="area" value={editForm.area} onChange={handleEditChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                      {editAreas.map((a, i) => <option key={i} value={a}>{a}</option>)}
                    </select>
                  ) : (
                    <Input name="area" value={editForm.area} onChange={handleEditChange} />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>City *</Label><Input name="city" value={editForm.city} onChange={handleEditChange} required /></div>
                <div className="space-y-2"><Label>State *</Label><Input name="state" value={editForm.state} onChange={handleEditChange} required /></div>
              </div>
              <div className="space-y-2"><Label>GST Number</Label><Input name="gst_number" value={editForm.gst_number} onChange={handleEditChange} /></div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditingVendor(null)}>Cancel</Button>
                <Button onClick={saveEditVendor} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-vendor">Save Changes</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="VENDOR" label="vendors" onComplete={() => { bulk.clearSelection(); fetchVendors(); }} onClear={bulk.clearSelection} />
    </div>
  );
};

export default VendorManagement;